/**
 * Input hygiene. Every string that reaches state or a chat broadcast goes
 * through one of these functions. They are pure and fully unit-tested.
 */

import { CONFIG, type GameConfig } from "./config.js";

export type ValidationResult<T> = { ok: true; value: T } | { ok: false; reason: string };

const NICKNAME_RE = /^[\p{L}\p{N} _-]{2,16}$/u;

/** Zero-width and bidi control characters that could spoof or hide content. */
const INVISIBLE_RE = /[\u200B-\u200F\u202A-\u202E\u2060\u2066-\u2069\uFEFF]/g;
/** C0/C1 control characters. */
const CONTROL_RE = /[\u0000-\u0008\u000B-\u001F\u007F-\u009F]/g;

/** Cheap UUID v4 shape check. The token is a bearer credential: never broadcast it. */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function isValidToken(value: unknown): value is string {
  return typeof value === "string" && UUID_RE.test(value);
}

/**
 * Normalize, collapse whitespace and validate a nickname.
 * Accepts letters (any script), digits, space, `_` and `-`, 2–16 chars.
 */
export function sanitizeNickname(raw: unknown): ValidationResult<string> {
  if (typeof raw !== "string") return { ok: false, reason: "NOT_A_STRING" };

  const stripped = raw.normalize("NFKC").replace(INVISIBLE_RE, "").replace(CONTROL_RE, "");
  const collapsed = stripped.trim().replace(/\s+/g, " ");

  if (collapsed.length === 0) return { ok: false, reason: "EMPTY" };
  if (collapsed.length < 2) return { ok: false, reason: "TOO_SHORT" };
  if (collapsed.length > 16) return { ok: false, reason: "TOO_LONG" };
  if (!NICKNAME_RE.test(collapsed)) return { ok: false, reason: "BAD_CHARS" };

  return { ok: true, value: collapsed };
}

/**
 * Sanitize a chat message: strip invisibles and control characters, collapse
 * runs of whitespace, trim and cap the length.
 */
export function sanitizeChat(raw: unknown, c: GameConfig = CONFIG): ValidationResult<string> {
  if (typeof raw !== "string") return { ok: false, reason: "NOT_A_STRING" };

  const stripped = raw
    .normalize("NFKC")
    .replace(INVISIBLE_RE, "")
    .replace(CONTROL_RE, "")
    .replace(/\s+/g, " ")
    .trim();

  if (stripped.length === 0) return { ok: false, reason: "EMPTY" };

  const value = stripped.length > c.CHAT_MAX_LEN ? stripped.slice(0, c.CHAT_MAX_LEN) : stripped;
  return { ok: true, value };
}

/** Append a uniqueness suffix to a display name: `Matteo`, `Matteo#2`, … */
export function withSuffix(name: string, alreadyTaken: (candidate: string) => boolean): string {
  if (!alreadyTaken(name)) return name;
  for (let i = 2; i < 1000; i++) {
    const candidate = `${name}#${i}`;
    if (!alreadyTaken(candidate)) return candidate;
  }
  return `${name}#${Date.now() % 1000}`;
}
